import React from "react";
import { useTranslation } from "react-i18next";
import { Props } from "./types";
import styles from "./styles.scss";
import {
  ExportsDataTypes,
  ExportsFormats,
  Filter as F,
  Identifiable,
  Project,
  Project as P,
  Zone as Z,
} from "@planzone/dto";
import { filter } from "@common/filter";
import { SortOptions } from "@pages/zonePage/projectsPage/types";
import {
  useAuthentication,
  useExportData,
  useRetrieveCurrentPlanzone,
  useRetrieveStrategicAxes,
  useZoneProjectsRetrieval,
  useRetrieveProjectListingConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { usePreferences } from "@components/preferences";
import { Loader } from "@common/loader";

function ExportButton(props: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { configuration } = useRetrieveProjectListingConfiguration(planzone);
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const roles = useRoles({});
  const { state } = usePreferences();
  const [isExporting, setIsExporting] = React.useState(false);
  const { zoneProjectsFilter: f } = state;
  const { exportData } = useExportData();
  const { account } = useAuthentication();
  const { strategicAxes } = useRetrieveStrategicAxes();
  const { projects, isRequestPending, retrieveZoneProjects } =
    useZoneProjectsRetrieval(props.zone, props.filter);
  const exportZone = (projects: Identifiable<Project>[]) => {
    const zone = Object.values(
      projects.reduce<{
        [key: string]: Identifiable<Z> & { projects: Identifiable<P>[] };
      }>(
        (zone, project) => ({
          ...zone,
          [project.zone.uuid]: zone[project.zone.uuid]
            ? {
                ...zone[project.zone.uuid],
                projects: [...zone[project.zone.uuid].projects, project],
              }
            : { ...project.zone, projects: [project] },
        }),
        {},
      ),
    );

    exportData({
      fileName: planzone.name,
      author: account,
      data: {
        type: ExportsDataTypes.ZONES_PROJECTS_REPORT,
        data: {
          planzone: planzone,
          zones: zone.map((zone) => ({
            ...zone,
            projects: projects,
          })),
          strategicAxis: strategicAxes,
          roles: roles,
          configuration: configuration,
          template: template,
          filter: f,
          sort: props.sort,
        },
      },
      format: ExportsFormats.EXCEL,
    });
  };

  useMessage((event) => {
    if (isExporting && event.type === events.retrieveZoneProjectsSucceeded) {
      exportZone(event.data.projects);
      setIsExporting(false);
    }
  });

  return (
    <button
      className={styles.exportButton}
      onClick={() => {
        if (!isRequestPending) {
          setIsExporting(true);
          retrieveZoneProjects({
            zone: props.zone,
            filter: props.filter,
          });
        }
      }}
    >
      {isRequestPending && isExporting ? (
        <div className={styles.icon}>
          <Loader />
        </div>
      ) : (
        <svg className={styles.icon} viewBox="0 0 23.57 20.953">
          <path d="M15.715,4.989a.979.979,0,0,0-.286-.692L11.422.286a.981.981,0,0,0-.7-.286h-.25V5.238h5.238ZM23.368,12.6,19.451,8.66a.656.656,0,0,0-1.121.462v2.664H15.711v2.619H18.33v2.668a.656.656,0,0,0,1.121.462l3.916-3.949A.7.7,0,0,0,23.368,12.6ZM7.857,13.751v-1.31a.657.657,0,0,1,.655-.655h7.2V6.548H10.149a.985.985,0,0,1-.982-.982V0H.982A.98.98,0,0,0,0,.982V19.971a.98.98,0,0,0,.982.982H14.733a.98.98,0,0,0,.982-.982V14.405h-7.2A.657.657,0,0,1,7.857,13.751Z" />
        </svg>
      )}
      <p className={styles.text}>{t("projectPage.listsPage.export")}</p>
    </button>
  );
}

export { ExportButton };
